/**
 * Sistema de comportamientos General + Eventos
 *
 * General: propiedades base que definen cómo es la criatura normalmente
 * Eventos: sobrescriben temporalmente algunas propiedades del General
 * Presets: plantillas iniciales OPCIONALES que rellenan varias propiedades a la vez
 *
 * Al exportar, el backend (exporter.py) convierte esto a Minecraft Bedrock real:
 * components + component_groups + events. Este archivo NUNCA genera el JSON final,
 * sólo define el catálogo visual + metadata de dependencias.
 */

// Lista de entidades vanilla de Minecraft Bedrock disponibles para "Transformarse".
// IDs reales (namespace minecraft:). No inventar entidades nuevas aquí.
export const VANILLA_ENTITIES = [
  { id: 'minecraft:allay', label_es: 'Allay' },
  { id: 'minecraft:armadillo', label_es: 'Armadillo' },
  { id: 'minecraft:axolotl', label_es: 'Ajolote' },
  { id: 'minecraft:bat', label_es: 'Murciélago' },
  { id: 'minecraft:bee', label_es: 'Abeja' },
  { id: 'minecraft:blaze', label_es: 'Blaze' },
  { id: 'minecraft:camel', label_es: 'Camello' },
  { id: 'minecraft:cat', label_es: 'Gato' },
  { id: 'minecraft:cave_spider', label_es: 'Araña de cueva' },
  { id: 'minecraft:chicken', label_es: 'Gallina' },
  { id: 'minecraft:cod', label_es: 'Bacalao' },
  { id: 'minecraft:cow', label_es: 'Vaca' },
  { id: 'minecraft:creeper', label_es: 'Creeper' },
  { id: 'minecraft:dolphin', label_es: 'Delfín' },
  { id: 'minecraft:donkey', label_es: 'Burro' },
  { id: 'minecraft:drowned', label_es: 'Ahogado' },
  { id: 'minecraft:enderman', label_es: 'Enderman' },
  { id: 'minecraft:fox', label_es: 'Zorro' },
  { id: 'minecraft:frog', label_es: 'Rana' },
  { id: 'minecraft:ghast', label_es: 'Ghast' },
  { id: 'minecraft:glow_squid', label_es: 'Calamar brillante' },
  { id: 'minecraft:goat', label_es: 'Cabra' },
  { id: 'minecraft:horse', label_es: 'Caballo' },
  { id: 'minecraft:husk', label_es: 'Husk' },
  { id: 'minecraft:iron_golem', label_es: 'Gólem de hierro' },
  { id: 'minecraft:llama', label_es: 'Llama' },
  { id: 'minecraft:magma_cube', label_es: 'Cubo de magma' },
  { id: 'minecraft:mooshroom', label_es: 'Mooshroom' },
  { id: 'minecraft:mule', label_es: 'Mula' },
  { id: 'minecraft:ocelot', label_es: 'Ocelote' },
  { id: 'minecraft:panda', label_es: 'Panda' },
  { id: 'minecraft:parrot', label_es: 'Loro' },
  { id: 'minecraft:phantom', label_es: 'Fantasma' },
  { id: 'minecraft:pig', label_es: 'Cerdo' },
  { id: 'minecraft:piglin', label_es: 'Piglin' },
  { id: 'minecraft:polar_bear', label_es: 'Oso polar' },
  { id: 'minecraft:pufferfish', label_es: 'Pez globo' },
  { id: 'minecraft:rabbit', label_es: 'Conejo' },
  { id: 'minecraft:salmon', label_es: 'Salmón' },
  { id: 'minecraft:sheep', label_es: 'Oveja' },
  { id: 'minecraft:shulker', label_es: 'Shulker' },
  { id: 'minecraft:silverfish', label_es: 'Lepisma plateado' },
  { id: 'minecraft:skeleton', label_es: 'Esqueleto' },
  { id: 'minecraft:slime', label_es: 'Slime' },
  { id: 'minecraft:sniffer', label_es: 'Sniffer' },
  { id: 'minecraft:snow_golem', label_es: 'Gólem de nieve' },
  { id: 'minecraft:spider', label_es: 'Araña' },
  { id: 'minecraft:squid', label_es: 'Calamar' },
  { id: 'minecraft:stray', label_es: 'Esqueleto errante' },
  { id: 'minecraft:strider', label_es: 'Strider' },
  { id: 'minecraft:tropicalfish', label_es: 'Pez tropical' },
  { id: 'minecraft:turtle', label_es: 'Tortuga' },
  { id: 'minecraft:vex', label_es: 'Vex' },
  { id: 'minecraft:villager_v2', label_es: 'Aldeano' },
  { id: 'minecraft:vindicator', label_es: 'Vindicator' },
  { id: 'minecraft:wandering_trader', label_es: 'Comerciante errante' },
  { id: 'minecraft:witch', label_es: 'Bruja' },
  { id: 'minecraft:wither_skeleton', label_es: 'Esqueleto del Wither' },
  { id: 'minecraft:wolf', label_es: 'Lobo' },
  { id: 'minecraft:zombie', label_es: 'Zombie' },
  { id: 'minecraft:zombie_villager_v2', label_es: 'Aldeano zombie' },
];

// Categorías de propiedades del comportamiento general
export const BEHAVIOR_CATEGORIES = [
  {
    id: 'identity',
    label_es: 'Identidad',
    label_ca: 'Identitat',
    label_en: 'Identity',
    icon: 'Sparkles',
    properties: [
      // Familia(s) de criatura — determina a quién puede atacar/afectar y filtros.
      // Valores oficiales Bedrock que el juego reconoce en filtros is_family.
      { id: 'family', label_es: 'Familia', label_ca: 'Família', label_en: 'Family', icon: 'Sparkles', type: 'multiselect', default: ['mob'], options: ['animal', 'monster', 'aquatic', 'undead', 'arthropod', 'mob', 'custom'] },
    ],
  },
  {
    id: 'stats',
    label_es: 'Estadísticas',
    label_ca: 'Estadístiques',
    label_en: 'Stats',
    icon: 'Heart',
    properties: [
      { id: 'maxHealth', label_es: 'Vida máxima', label_ca: 'Vida màxima', label_en: 'Max Health', icon: 'Heart', type: 'number', default: 20, min: 1, max: 1000, step: 1 },
      { id: 'invulnerable', label_es: 'Invulnerable', label_ca: 'Invulnerable', label_en: 'Invulnerable', icon: 'Shield', type: 'boolean', default: false },
      { id: 'fireResistance', label_es: 'Resistente al fuego', label_ca: 'Resistent al foc', label_en: 'Fire Resistance', icon: 'Flame', type: 'boolean', default: false },
    ],
  },
  {
    id: 'movement',
    label_es: 'Movimiento',
    label_ca: 'Moviment',
    label_en: 'Movement',
    icon: 'Move',
    properties: [
      { id: 'speed', label_es: 'Velocidad', label_ca: 'Velocitat', label_en: 'Speed', icon: 'Gauge', type: 'number', default: 0.25, min: 0, max: 2, step: 0.05 },
      { id: 'jumpHeight', label_es: 'Salto', label_ca: 'Salt', label_en: 'Jump', icon: 'ArrowUp', type: 'number', default: 0.42, min: 0, max: 3, step: 0.1 },
      { id: 'scale', label_es: 'Escala', label_ca: 'Escala', label_en: 'Scale', icon: 'Maximize', type: 'number', default: 1.0, min: 0.1, max: 10, step: 0.1 },
      { id: 'canClimb', label_es: 'Puede escalar', label_ca: 'Pot escalar', label_en: 'Can climb', icon: 'Mountain', type: 'boolean', default: false },
      { id: 'randomStroll', label_es: 'Pasear aleatoriamente', label_ca: 'Passejar aleatòriament', label_en: 'Random stroll', icon: 'Route', type: 'boolean', default: true },
      { id: 'canSwim', label_es: 'Nado', label_ca: 'Natació', label_en: 'Swim', icon: 'Waves', type: 'boolean', default: false },
      { id: 'swimType', label_es: 'Tipo de nado', label_ca: 'Tipus de natació', label_en: 'Swim type', icon: 'Waves', type: 'select', default: 'aquatic', options: ['aquatic', 'amphibious', 'aquatic_mammal'], showIf: 'canSwim' },
      { id: 'canFly', label_es: 'Vuelo', label_ca: 'Vol', label_en: 'Flight', icon: 'Feather', type: 'boolean', default: false },
      { id: 'flyType', label_es: 'Tipo de vuelo', label_ca: 'Tipus de vol', label_en: 'Flight type', icon: 'Feather', type: 'select', default: 'flying', options: ['flying', 'floating', 'hover'], showIf: 'canFly' },
      { id: 'flySpeed', label_es: 'Velocidad de vuelo', label_ca: 'Velocitat de vol', label_en: 'Flight speed', icon: 'Wind', type: 'number', default: 0.4, min: 0.05, max: 2, step: 0.05, showIf: 'canFly' },
    ],
  },
  {
    id: 'ai',
    label_es: 'Inteligencia',
    label_ca: 'Intel·ligència',
    label_en: 'AI',
    icon: 'Brain',
    properties: [
      { id: 'lookAtPlayer', label_es: 'Mirar jugadores', label_ca: 'Mirar jugadors', label_en: 'Look at players', icon: 'Eye', type: 'boolean', default: true },
      { id: 'followPlayer', label_es: 'Seguir dueño', label_ca: 'Seguir amo', label_en: 'Follow owner', icon: 'Footprints', type: 'boolean', default: false },
      { id: 'flee', label_es: 'Huir del jugador', label_ca: 'Fugir del jugador', label_en: 'Flee from player', icon: 'ArrowRightFromLine', type: 'boolean', default: false },
      { id: 'angryOnHurt', label_es: 'Al ser atacado: enfadarse', label_ca: 'En ser atacat: enfadar-se', label_en: 'When hurt: get angry', icon: 'Angry', type: 'boolean', default: false },
      { id: 'angryDuration', label_es: 'Segundos enfadado', label_ca: 'Segons enfadat', label_en: 'Angry seconds', icon: 'Timer', type: 'number', default: 10, min: 1, max: 120, step: 1, showIf: 'angryOnHurt' },
      { id: 'panic', label_es: 'Pánico al recibir daño', label_ca: 'Pànic en rebre dany', label_en: 'Panic when hurt', icon: 'AlertTriangle', type: 'boolean', default: false },
      { id: 'canTeleport', label_es: 'Puede teletransportarse', label_ca: 'Pot teletransportar-se', label_en: 'Can teleport', icon: 'Zap', type: 'boolean', default: false },
      { id: 'transformInto', label_es: 'Transformarse en', label_ca: 'Transformar-se en', label_en: 'Transform into', icon: 'Repeat', type: 'select', default: 'minecraft:zombie', options: ['self', ...VANILLA_ENTITIES.map((e) => e.id)] },
    ],
  },
  {
    id: 'targeting',
    label_es: 'Objetivos',
    label_ca: 'Objectius',
    label_en: 'Targets',
    icon: 'Target',
    properties: [
      // Tarjeta única: seleccionar directamente A QUIÉN persigue (sin booleano previo)
      { id: 'targetFor', label_es: 'Buscar objetivo', label_ca: 'Buscar objectiu', label_en: 'Search for target', icon: 'Target', type: 'select', default: 'player', options: ['player', 'monster', 'animal', 'mob', 'undead', 'aquatic', 'arthropod'] },
      { id: 'targetRange', label_es: 'Distancia de detección', label_ca: 'Distància de detecció', label_en: 'Detection range', icon: 'Search', type: 'number', default: 16, min: 1, max: 64, step: 1, showIf: 'targetFor' },
    ],
  },
  {
    id: 'combat',
    label_es: 'Combate',
    label_ca: 'Combat',
    label_en: 'Combat',
    icon: 'Swords',
    properties: [
      { id: 'canAttack', label_es: 'Puede atacar', label_ca: 'Pot atacar', label_en: 'Can attack', icon: 'Swords', type: 'boolean', default: false },
      { id: 'attackDamage', label_es: 'Daño', label_ca: 'Dany', label_en: 'Damage', icon: 'Sword', type: 'number', default: 4, min: 1, max: 100, step: 1, showIf: 'canAttack' },
      { id: 'attackRange', label_es: 'Alcance', label_ca: 'Abast', label_en: 'Range', icon: 'Target', type: 'number', default: 2, min: 1, max: 20, step: 0.5, showIf: 'canAttack' },
      { id: 'canShoot', label_es: 'Puede disparar', label_ca: 'Pot disparar', label_en: 'Can shoot', icon: 'Crosshair', type: 'boolean', default: false },
      { id: 'projectileType', label_es: 'Tipo de proyectil', label_ca: 'Tipus de projectil', label_en: 'Projectile type', icon: 'Zap', type: 'select', default: 'arrow', options: ['arrow', 'snowball', 'egg', 'trident', 'small_fireball', 'fireball', 'dragon_fireball', 'shulker_bullet', 'llama_spit', 'wither_skull', 'wind_charge', 'ender_pearl'], showIf: 'canShoot' },
      { id: 'shootRange', label_es: 'Alcance', label_ca: 'Abast', label_en: 'Range', icon: 'Target', type: 'number', default: 16, min: 2, max: 64, step: 1, showIf: 'canShoot' },
      { id: 'canExplode', label_es: 'Puede explotar', label_ca: 'Pot explotar', label_en: 'Can explode', icon: 'Bomb', type: 'boolean', default: false },
      { id: 'explosionRadius', label_es: 'Radio de explosión', label_ca: 'Radi d\'explosió', label_en: 'Explosion radius', icon: 'CircleDot', type: 'number', default: 3, min: 1, max: 10, step: 1, showIf: 'canExplode' },
    ],
  },
  {
    id: 'interaction',
    label_es: 'Interacción',
    label_ca: 'Interacció',
    label_en: 'Interaction',
    icon: 'Hand',
    properties: [
      { id: 'canInteract', label_es: 'Interactuar', label_ca: 'Interactuar', label_en: 'Interact', icon: 'Hand', type: 'boolean', default: false },
      { id: 'tameable', label_es: 'Domesticable', label_ca: 'Domesticable', label_en: 'Tameable', icon: 'Heart', type: 'boolean', default: false },
      { id: 'tameItem', label_es: 'Objeto para domesticar', label_ca: 'Objecte per domesticar', label_en: 'Tame item', icon: 'Wheat', type: 'select', default: 'wheat', options: ['wheat', 'bone', 'apple', 'carrot', 'golden_apple', 'salmon', 'cod', 'sweet_berries', 'cake', 'cookie'], showIf: 'tameable' },
      { id: 'tamedBehavior', label_es: 'Al domesticar', label_ca: 'En domesticar', label_en: 'When tamed', icon: 'Heart', type: 'select', default: 'follow', options: ['follow', 'sit', 'both'], showIf: 'tameable' },
      { id: 'rideable', label_es: 'Montable', label_ca: 'Muntable', label_en: 'Rideable', icon: 'User', type: 'boolean', default: false },
      { id: 'controllable', label_es: 'Controlable', label_ca: 'Controlable', label_en: 'Controllable', icon: 'Gamepad2', type: 'boolean', default: false, showIf: 'rideable' },
      { id: 'nameable', label_es: 'Puede tener nombre', label_ca: 'Pot tenir nom', label_en: 'Can have name', icon: 'Tag', type: 'boolean', default: true },
      { id: 'canPickupItems', label_es: 'Puede recoger objetos', label_ca: 'Pot recollir objectes', label_en: 'Can pickup items', icon: 'Package', type: 'boolean', default: false },
    ],
  },
  {
    id: 'automation',
    label_es: 'Temporizador',
    label_ca: 'Temporitzador',
    label_en: 'Timer',
    icon: 'Timer',
    properties: [
      { id: 'hasTimer', label_es: 'Temporizador', label_ca: 'Temporitzador', label_en: 'Timer', icon: 'Timer', type: 'boolean', default: false },
      { id: 'timerSeconds', label_es: 'Segundos', label_ca: 'Segons', label_en: 'Seconds', icon: 'Timer', type: 'number', default: 5, min: 1, max: 3600, step: 1, showIf: 'hasTimer' },
      { id: 'timerLooping', label_es: 'Repetir', label_ca: 'Repetir', label_en: 'Loop', icon: 'Repeat', type: 'boolean', default: false, showIf: 'hasTimer' },
    ],
  },
];

// Eventos disponibles — SÓLO triggers oficiales Bedrock/Education que existen realmente.
// Uniqueness: la mayoría de triggers sólo pueden existir UNA vez por entity en Bedrock
// (events es un dict por nombre). Se controla en la UI vía uniquePerEntity=true.
export const EVENT_TRIGGERS = [
  { id: 'on_hurt', label_es: 'Al recibir daño', label_ca: 'Al rebre dany', label_en: 'When hurt', icon: 'Swords', uniquePerEntity: true },
  { id: 'on_death', label_es: 'Al morir', label_ca: 'Al morir', label_en: 'When dies', icon: 'Skull', uniquePerEntity: true },
  { id: 'on_interact', label_es: 'Al interactuar', label_ca: 'A l\'interactuar', label_en: 'When interacts', icon: 'Hand', uniquePerEntity: true, requiresGeneral: 'canInteract' },
  { id: 'on_day', label_es: 'De día', label_ca: 'De dia', label_en: 'At day', icon: 'Sun', uniquePerEntity: true },
  { id: 'on_night', label_es: 'De noche', label_ca: 'De nit', label_en: 'At night', icon: 'Moon', uniquePerEntity: true },
  { id: 'on_timer', label_es: 'Temporizador', label_ca: 'Temporitzador', label_en: 'Timer', icon: 'Timer', uniquePerEntity: true, requiresGeneral: 'hasTimer' },
  // Extras oficiales Bedrock ya verificados en iteraciones anteriores (no forman parte
  // del MVP del spec pero son reales y funcionan — se conservan como "avanzado").
  { id: 'on_ignite', label_es: 'Al encenderse', label_ca: 'En encendre\'s', label_en: 'When ignited', icon: 'Flame', uniquePerEntity: true },
  { id: 'on_in_water', label_es: 'Al entrar en agua', label_ca: 'En entrar a l\'aigua', label_en: 'When enters water', icon: 'Waves', uniquePerEntity: true },
  { id: 'on_on_fire', label_es: 'Al estar en llamas', label_ca: 'En estar en flames', label_en: 'When on fire', icon: 'Flame', uniquePerEntity: true },
];

// ============================================================================
// PRESETS — plantillas iniciales OPCIONALES. El usuario puede elegir uno o
// empezar en blanco (Personalizado). Aplican varias propiedades de golpe pero
// NUNCA sobrescriben lo que el usuario ya haya tocado.
// ============================================================================
export const PRESETS = [
  {
    id: 'passive',
    emoji: '🟢',
    label_es: 'Pasivo', label_ca: 'Passiu', label_en: 'Passive',
    description_es: 'Animal tranquilo que pasea y no ataca (oveja, vaca, cerdo).',
    general: { family: ['animal'], maxHealth: 10, speed: 0.2, randomStroll: true, lookAtPlayer: true, panic: true, nameable: true },
  },
  {
    id: 'monster',
    emoji: '🔴',
    label_es: 'Monstruo', label_ca: 'Monstre', label_en: 'Monster',
    description_es: 'Criatura hostil que busca y ataca al jugador (zombie, esqueleto).',
    general: { family: ['monster'], maxHealth: 20, speed: 0.23, randomStroll: true, lookAtPlayer: true, targetFor: 'player', targetRange: 16, canAttack: true, attackDamage: 4, attackRange: 2 },
  },
  {
    id: 'pet',
    emoji: '❤️',
    label_es: 'Mascota', label_ca: 'Mascota', label_en: 'Pet',
    description_es: 'Se puede domesticar y sigue a su dueño (perro, gato).',
    general: { family: ['animal'], maxHealth: 10, speed: 0.25, randomStroll: true, lookAtPlayer: true, tameable: true, tameItem: 'bone', tamedBehavior: 'follow', followPlayer: true, nameable: true },
  },
  {
    id: 'aquatic',
    emoji: '🌊',
    label_es: 'Acuático', label_ca: 'Aquàtic', label_en: 'Aquatic',
    description_es: 'Vive y nada en el agua (pez, delfín).',
    general: { family: ['aquatic'], maxHealth: 6, speed: 0.12, canSwim: true, swimType: 'aquatic', lookAtPlayer: true },
  },
  {
    id: 'flying',
    emoji: '🪽',
    label_es: 'Volador', label_ca: 'Volador', label_en: 'Flying',
    description_es: 'Se mueve volando en lugar de caminar.',
    general: { maxHealth: 10, canFly: true, flyType: 'flying', flySpeed: 0.4, lookAtPlayer: true },
  },
  {
    id: 'custom',
    emoji: '🏷️',
    label_es: 'Personalizado', label_ca: 'Personalitzat', label_en: 'Custom',
    description_es: 'Empieza en blanco y añade tú mismo cada comportamiento.',
    general: {},
  },
];

/**
 * Devuelve el valor de una propiedad con default si no está definida
 */
export const getPropValue = (behaviors, categoryId, propId) => {
  const category = BEHAVIOR_CATEGORIES.find((c) => c.id === categoryId);
  if (!category) return undefined;
  const prop = category.properties.find((p) => p.id === propId);
  if (!prop) return undefined;
  const value = behaviors.general?.[propId];
  return value !== undefined ? value : prop.default;
};

// ============================================================================
// METADATA DE BEDROCK — Documentación oficial Microsoft Learn
//   https://learn.microsoft.com/es-mx/minecraft/creator/reference/content/entityreference/examples/componentlist
// ============================================================================
export const BEHAVIOR_METADATA = {
  // ========== IDENTITY ==========
  family: {
    mcId: 'minecraft:type_family',
    description_es: 'Familia(s) de la criatura. Afecta filtros de objetivo, spawn y si aparece en Pacífico (monster).',
    status: 'stable',
  },

  // ========== STATS ==========
  maxHealth: { mcId: 'minecraft:health', description_es: 'Vida máxima. Se muestra como corazones en Minecraft.', status: 'stable' },
  invulnerable: { mcId: 'minecraft:damage_sensor', description_es: 'La criatura no recibe daño de ningún tipo.', status: 'stable', conflicts: ['canAttack'] },
  fireResistance: { mcId: 'minecraft:fire_immune', description_es: 'Inmune al fuego, lava y quemaduras.', status: 'stable' },

  // ========== MOVEMENT ==========
  speed: { mcId: 'minecraft:movement', description_es: 'Velocidad base al caminar. 0.25 = velocidad normal de un mob.', status: 'stable' },
  jumpHeight: { mcId: 'minecraft:jump.static', description_es: 'Altura del salto. 0.42 es el valor de la mayoría de mobs.', status: 'stable' },
  scale: { mcId: 'minecraft:scale', description_es: 'Multiplicador de tamaño. 1.0 = tamaño normal.', status: 'stable' },
  canClimb: { mcId: 'minecraft:can_climb', conflicts: ['canFly'], description_es: 'Puede trepar por paredes verticales (como las arañas).', status: 'stable' },
  randomStroll: { mcId: 'minecraft:behavior.random_stroll', description_es: 'Pasea aleatoriamente por su entorno.', status: 'stable' },
  canSwim: {
    mcId: 'minecraft:navigation.swim',
    conflicts: ['canFly'],
    description_es: 'La criatura nada en vez de caminar. Elige el tipo de nado abajo.',
    status: 'stable',
  },
  swimType: { requires: ['canSwim'], description_es: 'Acuático (sólo agua), Anfibio (agua y tierra) o Mamífero acuático (necesita respirar).', status: 'stable' },
  canFly: {
    mcId: 'minecraft:navigation.fly',
    conflicts: ['canSwim', 'canClimb'],
    description_es: 'La criatura vuela en vez de caminar. Elige el tipo de vuelo abajo.',
    status: 'stable',
  },
  flyType: { requires: ['canFly'], description_es: 'Volador (libre), Flotante (como un fantasma) o Hover (se queda flotando, como una abeja).', status: 'stable' },
  flySpeed: { requires: ['canFly'], mcId: 'minecraft:movement', description_es: 'Velocidad al volar.', status: 'stable' },

  // ========== AI ==========
  lookAtPlayer: { mcId: 'minecraft:behavior.look_at_player', description_es: 'Mira al jugador cuando está cerca.', status: 'stable' },
  followPlayer: {
    mcId: 'minecraft:behavior.follow_owner',
    requires: ['tameable'],
    description_es: 'Sigue a su dueño tras ser domesticado. Requiere que sea Domesticable.',
    status: 'stable',
  },
  flee: { mcId: 'minecraft:behavior.avoid_mob_type', description_es: 'Huye del jugador cuando se acerca.', status: 'stable', conflicts: ['canAttack'] },
  angryOnHurt: {
    mcId: 'minecraft:angry',
    description_es: 'Al recibir daño se enfada: si no ataca siempre, empieza a perseguir y atacar temporalmente.',
    status: 'stable',
  },
  angryDuration: { requires: ['angryOnHurt'], description_es: 'Segundos que dura enfadada tras recibir daño.', status: 'stable' },
  panic: { mcId: 'minecraft:behavior.panic', description_es: 'Entra en pánico al recibir daño.', status: 'stable' },
  canTeleport: { mcId: 'minecraft:teleport', description_es: 'Puede teletransportarse (como el enderman).', status: 'stable' },
  transformInto: { mcId: 'minecraft:transformation', description_es: 'Se transforma en otra criatura ("Esta criatura" o una criatura vanilla).', status: 'stable' },

  // ========== TARGETING ==========
  targetFor: {
    mcId: 'minecraft:behavior.nearest_attackable_target',
    description_es: 'La criatura busca y persigue este tipo de objetivo. Necesario para Atacar o Disparar.',
    status: 'stable',
  },
  targetRange: { requires: ['targetFor'], description_es: 'Radio máximo en bloques para detectar objetivos.', status: 'stable' },

  // ========== COMBAT ==========
  canAttack: {
    mcId: 'minecraft:attack',
    requires: ['targetFor'],
    autoAdds: ['minecraft:behavior.melee_attack'],
    conflicts: ['flee', 'invulnerable'],
    description_es: 'Ataca cuerpo a cuerpo al objetivo. Requiere Buscar objetivo.',
    status: 'stable',
  },
  attackDamage: { requires: ['canAttack'], description_es: 'Daño (medios corazones) por golpe.', status: 'stable' },
  attackRange: { requires: ['canAttack'], description_es: 'Distancia máxima de ataque.', status: 'stable' },
  canShoot: {
    mcId: 'minecraft:shooter',
    requires: ['targetFor'],
    autoAdds: ['minecraft:behavior.ranged_attack'],
    description_es: 'Dispara proyectiles al objetivo. Requiere Buscar objetivo.',
    status: 'stable',
  },
  projectileType: { requires: ['canShoot'], description_es: 'Proyectil oficial de Bedrock.', status: 'stable' },
  shootRange: { requires: ['canShoot'], description_es: 'Radio máximo de disparo.', status: 'stable' },
  canExplode: {
    mcId: 'minecraft:explode',
    autoAdds: ['minecraft:interact'],
    description_es: 'Explota como un creeper. También explota si un jugador le acerca un mechero (Flint & Steel).',
    status: 'stable',
  },
  explosionRadius: { requires: ['canExplode'], description_es: 'Potencia de la explosión.', status: 'stable' },

  // ========== INTERACTION ==========
  canInteract: {
    mcId: 'minecraft:interact',
    description_es: 'El jugador puede hacer clic-derecho sobre la criatura. Combínalo con un evento "Al interactuar".',
    status: 'stable',
  },
  tameable: { mcId: 'minecraft:tameable', description_es: 'Puede ser domesticado dando un item.', status: 'stable' },
  tameItem: { requires: ['tameable'], description_es: 'Item que domestica a la criatura.', status: 'stable' },
  tamedBehavior: { requires: ['tameable'], description_es: 'Qué hace al domesticarse: seguir, sentarse, o ambos.', status: 'stable' },
  rideable: { mcId: 'minecraft:rideable', description_es: 'El jugador puede montarse sobre la criatura.', status: 'stable' },
  controllable: {
    requires: ['rideable'],
    mcId: 'minecraft:input_ground_controlled',
    description_es: 'El jugador puede mover a la criatura mientras la monta. Requiere Montable.',
    status: 'stable',
  },
  nameable: { mcId: 'minecraft:nameable', description_es: 'Se puede renombrar con una etiqueta.', status: 'stable' },
  canPickupItems: { mcId: 'minecraft:behavior.pickup_items', description_es: 'Recoge objetos del suelo automáticamente.', status: 'stable' },

  // ========== AUTOMATION ==========
  hasTimer: {
    mcId: 'minecraft:timer',
    description_es: 'Cuenta el tiempo indicado y dispara el evento Temporizador al terminar.',
    status: 'stable',
  },
  timerSeconds: { requires: ['hasTimer'], description_es: 'Segundos hasta que se dispare el evento.', status: 'stable' },
  timerLooping: { requires: ['hasTimer'], description_es: 'Si está activo, el temporizador se repite indefinidamente.', status: 'stable' },
};

// Metadata de eventos/triggers — SÓLO los realmente soportados por Bedrock
export const EVENT_METADATA = {
  on_hurt: { mcTrigger: 'minecraft:on_hurt', description_es: 'Al recibir daño.', status: 'stable' },
  on_death: { mcTrigger: 'minecraft:on_death', description_es: 'Al morir.', status: 'stable' },
  on_interact: { mcTrigger: 'minecraft:interact', description_es: 'Al clic-derecho de un jugador. Requiere activar "Interactuar".', status: 'stable' },
  on_day: { mcTrigger: 'minecraft:environment_sensor', description_es: 'De día — usa environment_sensor con filtro is_daytime.', status: 'stable' },
  on_night: { mcTrigger: 'minecraft:environment_sensor', description_es: 'De noche — usa environment_sensor con filtro is_daytime=false.', status: 'stable' },
  on_timer: { mcTrigger: 'minecraft:timer', description_es: 'Cuando el Temporizador llega a cero. Requiere activar "Temporizador".', status: 'stable' },
  on_ignite: { mcTrigger: 'minecraft:on_ignite', description_es: 'Al ser encendida (fuego).', status: 'stable' },
  on_in_water: { mcTrigger: 'minecraft:environment_sensor', description_es: 'En agua — usa environment_sensor con filtro is_in_water.', status: 'stable' },
  on_on_fire: { mcTrigger: 'minecraft:environment_sensor', description_es: 'En llamas — usa environment_sensor con filtro is_on_fire.', status: 'stable' },
};

/**
 * Devuelve la lista de propiedades que este propId REQUIERE que estén activas
 * (dependencias directas + showIf implícito).
 */
export const getRequirements = (propId) => {
  const meta = BEHAVIOR_METADATA[propId];
  const reqs = new Set(meta?.requires || []);
  for (const cat of BEHAVIOR_CATEGORIES) {
    for (const p of cat.properties) {
      if (p.id === propId && p.showIf) {
        reqs.add(p.showIf);
      }
    }
  }
  return Array.from(reqs);
};

/**
 * Devuelve la lista de propiedades con las que este propId es incompatible.
 */
export const getConflicts = (propId) => {
  const meta = BEHAVIOR_METADATA[propId];
  return meta?.conflicts || [];
};

/**
 * Comprueba si un propId puede ser añadido dado el estado actual de props activas.
 * Devuelve { ok: boolean, missingRequires: [...], conflictsWith: [...] }
 */
export const checkAvailability = (propId, activePropIds) => {
  const reqs = getRequirements(propId);
  const conflicts = getConflicts(propId);
  const active = new Set(activePropIds);
  const missingRequires = reqs.filter((r) => !active.has(r));
  const conflictsWith = conflicts.filter((c) => active.has(c));
  return {
    ok: missingRequires.length === 0 && conflictsWith.length === 0,
    missingRequires,
    conflictsWith,
  };
};
